import { WidgetStreamState } from 'widgets/store/stream/WidgetStreamState';

import timeSeriesReducer from 'utils/timeseries/timeSeriesUtils';
import {
    StreamDisableRangeElement,
    StreamMessageElement,
} from 'communication/types/StreamElement';
import streamMessagesReducer from 'utils/messages/StreamMessage';

import { WidgetType } from 'widgets/WidgetType';

import { selectPortfolioPlState } from 'widgets/portfolioPl/store/selectors';
import {
    initialPortfolioPlStreamState,
    PortfolioPlDataState,
    PortfolioPlDataTimeSeriesItem,
} from 'widgets/portfolioPl/store/stream/PortfolioPlWidgetStreamState';
import {
    buildStreamSlice,
    useStreamSlice,
    WidgetStreamSliceActionPayloadType,
} from 'widgets/store/stream/streamSlice';
import {
    PortfolioPlDataElement,
    PortfolioPlElement,
    PortfolioPlTimeElement,
    PortfolioPlTimeSeriesElement,
} from 'communication/types/portfolioPl/PortfolioPlElement';
import disableRangeReducer from 'widgets/store/disableRange/StreamDisableRangeDataState';
import { mapSessionBoundary } from 'utils/timeseries/TimeSeriesDisableRange';
import omitUndefined from 'utils/omitUndefined';

const processStreamElement = (
    element: PortfolioPlDataElement,
    nextState: WidgetStreamState<PortfolioPlDataState>
) => {
    const nextDataState: PortfolioPlDataState = nextState.data;
    timeSeriesReducer(
        element as PortfolioPlTimeSeriesElement,
        nextDataState,
        mapToPortfolioPlDataTimeSeriesItemFromElement
    );
    streamMessagesReducer(element as StreamMessageElement, nextState);
    disableRangeReducer(element as StreamDisableRangeElement, nextDataState);
};

export const buildPortfolioPlStreamSlice = () =>
    buildStreamSlice<
        PortfolioPlDataState,
        PortfolioPlDataElement,
        PortfolioPlElement
    >(
        WidgetType.PORTFOLIO_PL,
        selectPortfolioPlState,
        initialPortfolioPlStreamState,
        processStreamElement
    );

export const usePortfolioPlStreamSlice = () =>
    useStreamSlice(
        WidgetType.PORTFOLIO_PL,
        selectPortfolioPlState,
        initialPortfolioPlStreamState,
        processStreamElement
    );

export const usePortfolioPlStreamData = () => {
    return usePortfolioPlStreamSlice().streamData;
};

export type PortfolioPlStreamPayloadType =
    WidgetStreamSliceActionPayloadType<PortfolioPlDataElement>;

function mapToPortfolioPlDataTimeSeriesItemFromElement(
    element: PortfolioPlTimeElement
): PortfolioPlDataTimeSeriesItem {
    return omitUndefined({
        realizedPnlBuys: element.brplB,
        realizedPnlSells: element.srplB,
        realizedPnlBuysAndSells: element.rplB,

        unrealizedPnlBuys: element.buplB,
        unrealizedPnlSells: element.suplB,
        unrealizedPnlBuysAndSells: element.uplB,

        totalPnlBuys: element.btplB,
        totalPnlSells: element.stplB,
        totalPnlBuysAndSells: element.tplB,

        indexPnl: element.idxN,

        buyUnrealizedBenchmark: element.bub,
        sellUnrealizedBenchmark: element.sub,

        percentCompleteBuysAndSells: element.pct,
        percentCompleteBuys: element.bpct,
        percentCompleteSells: element.spct,

        ...mapSessionBoundary(element),

        _buyCumulativeQuantity: element.bcq,
        _buyRealizedBenchmark: element.brb,
        _buyRealizedPnl: element.brp,
        _buyRemainingQuantity: element.brq,
        _buyUnrealizedPnl: element.bup,
        _sellCumulativeQuantity: element.scq,
        _sellRealizedBenchmark: element.srb,
        _sellRealizedPnl: element.srp,
        _sellRemainingQuantity: element.srq,
        _sellUnrealizedPnl: element.sup,
        _totalRealizedPnl: element.trp,
        _totalUnrealizedPnl: element.tup,
        _totalPnl: element.ttp,
    });
}
